import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";
import "./HomeSettings.css";

const HomeSettings = () => {
  const [section, setSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null); // { file, preview, type }
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSection();
  }, []);

  const fetchSection = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/sentinix/homesettings");
      const data = Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data];

      const hero1 = data.find((s) => s.sectionType === "hero1");

      if (hero1) {
        setSection(hero1);
      } else {
        const res = await api.post("/sentinix/homesettings", {
          sectionType: "hero1",
          title: "Hero 1 Title",
          description: "Hero 1 Description text...",
          imageKey: "",
          isActive: true,
        });
        setSection(res.data.data);
      }
    } catch (err) {
      console.error(err);
      setMessage("Error loading section");
    }
    setIsLoading(false);
  };

  const handleMediaSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");

    if (isVideo) {
      const preview = URL.createObjectURL(file);
      setSelectedMedia({ file, preview, type: "video" });
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedMedia({ file, preview: reader.result, type: "image" });
      };
      reader.readAsDataURL(file);
    }
  };

  const existingIsVideo = (url) => {
    if (!url) return false;
    if (url.includes("/video/upload/")) return true;
    const ext = url.split(".").pop().split("?")[0].toLowerCase();
    return ["mp4", "webm", "ogg", "mov", "avi"].includes(ext);
  };

  const handleUpdate = async () => {
    setIsSaving(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("title", section.title || "");
      formData.append("description", section.description || "");
      formData.append("sectionType", "hero1");
      formData.append("isActive", "true");

      if (selectedMedia) {
        formData.append("imageKey", selectedMedia.file);
      } else if (section.imageKey && typeof section.imageKey === "string") {
        formData.append("imageKey", section.imageKey);
      }

      const res = await api.put(
        `/sentinix/homesettings/${section.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.success) {
        setEditing(false);
        setSelectedMedia(null);
        fetchSection();
        setMessage("Section updated successfully");
      } else {
        setMessage(res.data.error || "Update failed");
      }
    } catch (err) {
      console.error("Update error:", err);
      setMessage(err.response?.data?.message || "Update failed");
    }

    setIsSaving(false);
    setTimeout(() => setMessage(""), 3000);
  };

  if (isLoading) {
    return (
      <div className="home-settings-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Home Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-settings-container">
      <h1 className="page-title-home">Home Settings</h1>

      {message && (
        <div className={`message ${message.includes("success") ? "success" : "error"}`}>
          {message}
        </div>
      )}

      {section && (
        <div className="section-card">
          {/* Header */}
          <div className="section-header">
            <h2>Home Hero Section 1</h2>
            {!editing && (
              <button onClick={() => setEditing(true)} className="btn-edit">
                Edit
              </button>
            )}
          </div>

          <div className="section-body">
            {/* Title */}
            <div className="form-group-home">
              <label>Section Title</label>
              {editing ? (
                <input
                  type="text"
                  value={section.title || ""}
                  onChange={(e) =>
                    setSection({ ...section, title: e.target.value })
                  }
                  className="form-input"
                />
              ) : (
                <p className="field-value">{section.title}</p>
              )}
            </div>

            {/* Description */}
            <div className="form-group-home">
              <label>Section Description</label>
              {editing ? (
                <textarea
                  rows={4}
                  value={section.description || ""}
                  onChange={(e) =>
                    setSection({ ...section, description: e.target.value })
                  }
                  className="form-textarea"
                />
              ) : (
                <p className="field-value">{section.description}</p>
              )}
            </div>

            {/* ✅ Media — Image or Video */}
            <div className="form-group-home">
              <label>Section Media (Image or Video)</label>

              {/* ✅ New selected media preview */}
              {selectedMedia && (
                <div className="image-preview">
                  {selectedMedia.type === "video" ? (
                    <video
                      src={selectedMedia.preview}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        maxHeight: "300px",
                      }}
                    />
                  ) : (
                    <img src={selectedMedia.preview} alt="Preview" />
                  )}
                </div>
              )}

              {!selectedMedia && section.imageKey && (
                <div className="image-preview">
                  {existingIsVideo(section.imageKey) ? (
                    <video
                      src={section.imageKey}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        maxHeight: "300px",
                      }}
                    />
                  ) : (
                    <img src={section.imageKey} alt="Hero Section" />
                  )}
                </div>
              )}
            {editing && (
              <div className="image-upload">
                {/* File input wrapper with custom button */}
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    id="media-upload"
                    accept="image/*,video/*"
                    onChange={handleMediaSelect}
                    className="file-input-hidden"
                  />
                  <label htmlFor="media-upload" className="file-upload-label">
                    📁 Choose File
                  </label>
                  
                  {/* Show selected file name */}
                  {selectedMedia && (
                    <span className="file-name">{selectedMedia.file.name}</span>
                  )}
                  {!selectedMedia && section.imageKey && (
                    <span className="file-name">
                      Current {existingIsVideo(section.imageKey) ? "video" : "image"} will be kept
                    </span>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Footer */}
          {editing && (
            <div className="section-footer">
              <button
                onClick={handleUpdate}
                disabled={isSaving}
                className="btn-save"
              >
                {isSaving ? "Saving..." : "💾 Save Changes"}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setSelectedMedia(null);
                  fetchSection();
                }}
                className="btn-cancel"
              >
                ❌ Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HomeSettings;